import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import { observer } from 'mobx-react-lite';

import {
    Config,
    isWidget,
    PrimaryButton,
    MessagePropType,
    AlternateAnchor,
    getLanguageFromLocale,
    DefaultIntlFormatValues,
    getAffiliateDecoratedURL,
} from '@ratehub/base-ui';

import MESSAGES from '../definitions/Messages';
import PRODUCT_TYPES from '../definitions/ProductTypes';
import CreditCardSynopsisShape from '../definitions/CreditCardSynopsisShape';
import getEligibilityCheckerURL from '../functions/getEligibilityCheckerURL';
import isCardEligibleForReferralOffer from '../functions/isCardEligibleForReferralOffer';


function CreditCardCTA({
    onClick,
    applyText,
    buttonSize = 'medium',

    className,

    cardTypes,
    giftCardOffer,
    hasReferralOffer = false,
    cardId,

    hasEligibilityCTA = true,

    children,

    productType,
    ...otherProps
}) {
    const intl = useIntl();
    const [ isEligibleForReferralOffer, setIsEligibleForReferralOffer ] = useState(false);
    // the main CTA label may depend on a cookie in the browser, which won’t be available during exporting
    useEffect(() => {
        setIsEligibleForReferralOffer(isCardEligibleForReferralOffer({ hasGiftOffer: !!giftCardOffer, hasReferralOffer }));
    }, [ JSON.stringify(giftCardOffer), hasReferralOffer ]);

    const language = getLanguageFromLocale(intl.locale);
    const isAWidget = isWidget();
    const isViewOfferCTA = !isAWidget && (giftCardOffer || isEligibleForReferralOffer);

    // TODO: Remove comparison to secured card when long term fix is implemented
    const displayEligibilityCTA = hasEligibilityCTA && !cardTypes.includes(PRODUCT_TYPES.SECURED);

    // eslint-disable-next-line jsx-control-statements/jsx-use-if-tag
    const primaryButtonMessage = isViewOfferCTA
        ? (
            <FormattedMessage
                id="edb.cc.cta.incentive.view-offer"
                defaultMessage="View offer"
            />
        )
        : applyText || MESSAGES.ctaButtonText;

    /*
        Atypical Event Clicking
        - when there are children (as for featured products), they render above the CTA(s)
        - in that case, a click anywhere on <Container> is seen as a CTA click on the "go to site" button, handled
          by onClick (which accounts for handling gift card offers)
        - however, when there are two CTAs, the additional one is an anchor and wants to avoid the "go to site"
          logic, so it includes an event.stopPropagation()
    */

    const ButtonApplyNow = (
        <PrimaryButton
            className="cc-cta"
            message={primaryButtonMessage}
            onClick={children
                ? () => {}
                : onClick
            }
            size={buttonSize}
            data-name="credit-cards.apply-now"
            key="theButton"
        />
    );

    const AnchorEligibilityChecker = (
        <AlternateAnchor
            className="cc-cta"
            message={(
                <FormattedMessage
                    id="edb.cc.check-eligibility-cta"
                    defaultMessage="check eligibility"
                    values={{
                        ...DefaultIntlFormatValues,
                    }}
                />
            )}
            onClick={event => event.stopPropagation()}
            href={getAffiliateDecoratedURL(getEligibilityCheckerURL(language, cardId, productType))}
            target={isAWidget ? '_blank' : ''}
            size={buttonSize}
            data-name="credit-cards.check-eligibility"
            key="theAnchor"
        />
    );


    return (
        <>
            <Container
                className={className}
                onClick={children ? onClick : undefined}
                {...otherProps}
            >
                {children}

                {/* always wrap buttons inside a .cta to satisfy grid placement requirements of ProductCardBrief */}
                <div
                    className={classNames(
                        'cta',  // inappropriate familiarity… used by ProductCardBrief for placing element in its grid
                        'cta-wrapper rh-display-flex rh-flex-wrap-reverse rh-justify-content-center rh-gap-0_5',
                    )}
                >
                    <Choose>
                        <When condition={displayEligibilityCTA && Config.ENABLE_CC_CHECK_ELIGIBILITY_CTA}>
                            {AnchorEligibilityChecker}
                            {ButtonApplyNow}
                        </When>

                        <Otherwise>
                            {ButtonApplyNow}
                        </Otherwise>
                    </Choose>
                </div>
            </Container>
        </>
    );
}


CreditCardCTA.propTypes = {
    onClick: PropTypes.func,
    applyText: MessagePropType,
    buttonSize: PropTypes.oneOf([ 'small', 'medium', 'large' ]),

    className: PropTypes.string,

    cardTypes: CreditCardSynopsisShape.cardTypes,
    giftCardOffer: CreditCardSynopsisShape.giftCardOffer,
    hasReferralOffer: PropTypes.bool,
    cardId: CreditCardSynopsisShape.id,

    children: PropTypes.node,

    hasEligibilityCTA: PropTypes.bool,
    productType: PropTypes.oneOf(Object.values(PRODUCT_TYPES)),
};


const Container = styled.div`
    > .cta-wrapper {
        > .cc-cta {
            // we do _not_ want our text to wrap within the button or link
            white-space: nowrap;
            max-width: 18rem;
        }
    }
`;


export default observer(CreditCardCTA);
